import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { marked } from 'marked';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import {
  BCSaaSOrganizationSettingEntity,
  BCSaaSSettingEntity,
} from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

/**
 * A single generated KB section from the Setup Agent
 */
interface GeneratedSection {
  settingKey: string;
  settingName: string;
  generatedValue: string;
  confidence: 'high' | 'medium' | 'low';
  confidenceReason: string;
  sourceUrls: string[];
}

/**
 * Crawl summary from the agent
 */
interface CrawlSummary {
  urlsFetched: string[];
  urlsFailed: string[];
  searchQueries: string[];
}

/**
 * Per-section review state in the UI
 */
interface SectionReviewState {
  section: GeneratedSection;
  accepted: boolean;
  isEditing: boolean;
  editedValue: string;
  renderedHtml: SafeHtml;
  isExpanded: boolean;
  showSources: boolean;
}

/**
 * Setup Agent Component
 *
 * Provides an interface for generating knowledge base content from an organization's
 * website using the Setup KB Generator AI agent. Allows admin review of generated
 * content before saving to organization settings.
 */
@Component({
  selector: 'izzy-setup-agent',
  standalone: false,
  templateUrl: './setup-agent.component.html',
  styleUrls: ['./setup-agent.component.css']
})
export class SetupAgentComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {

  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  // Input state
  protected websiteUrl: string = '';
  protected organizationName: string = '';

  // Running state
  protected isRunning: boolean = false;
  protected progressMessage: string = '';

  // Results state
  protected sectionStates: SectionReviewState[] = [];
  protected crawlSummary: CrawlSummary | null = null;
  protected lastRunAt: string | null = null;
  protected hasResults: boolean = false;

  // Error state
  protected errorMessage: string = '';

  // Pending website sync state
  protected hasPendingSync: boolean = false;
  protected pendingSyncDate: string | null = null;
  private pendingSyncResultId: string | null = null;
  private pendingSyncResultJSON: string | null = null;

  // Internal
  private selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  private setupAgentId: string | null = null;
  private settings: BCSaaSSettingEntity[] = [];
  private organizationScopeId: string | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      await this.initialize();
    }
  }

  private async initialize(): Promise<void> {
    if (!this.organizationId) return;

    await IzzyMetadataEngine.Instance.Config(false);
    this.settings = IzzyMetadataEngine.Instance.Settings;

    // Find the organization
    this.selectedOrganization = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations().find(
      o => o.ID === this.organizationId
    ) || null;

    if (this.selectedOrganization) {
      this.organizationName = this.selectedOrganization.Name;
      if (this.selectedOrganization.Website) {
        this.websiteUrl = this.selectedOrganization.Website;
      }
    }

    // Look up the 'Organization' scope ID
    await this.findOrganizationScopeId();

    // Find the Setup KB Generator agent ID
    await this.findSetupAgentId();

    // Load last run timestamp
    await this.loadLastRunAt();

    // Load any pending website sync result awaiting review
    await this.loadPendingSync();

    this.emitStatus();
  }

  private async findOrganizationScopeId(): Promise<void> {
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'BC: Setting Scopes',
      ExtraFilter: "Name = 'Organization'",
      ResultType: 'simple'
    });
    if (result?.Success && result.Results.length > 0) {
      this.organizationScopeId = (result.Results[0] as { ID: string }).ID;
    }
  }

  private async findSetupAgentId(): Promise<void> {
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'MJ: AI Agents',
      ExtraFilter: "Name = 'Setup KB Generator'",
      ResultType: 'simple'
    });
    if (result?.Success && result.Results.length > 0) {
      this.setupAgentId = (result.Results[0] as { ID: string }).ID;
    }
  }

  private async loadLastRunAt(): Promise<void> {
    if (!this.organizationId) return;

    const rv = new RunView();
    let settingId = IzzyMetadataEngine.Instance.Settings.find(s => s.Key === 'SetupAgentLastRunAt')?.ID;
    if (!settingId) {
      const settingRes = await rv.RunView<{ ID: string }>({
        EntityName: 'BC: Settings',
        ExtraFilter: "[Key] = 'SetupAgentLastRunAt'",
        Fields: ['ID'],
        ResultType: 'simple'
      });
      settingId = settingRes?.Success ? settingRes.Results?.[0]?.ID : undefined;
    }
    if (!settingId) return;

    const result = await rv.RunView<BCSaaSOrganizationSettingEntity>({
      EntityName: 'BC: Organization Settings',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND SettingID = '${settingId}'`,
      ResultType: 'entity_object'
    });

    if (result?.Success && result.Results.length > 0) {
      this.lastRunAt = result.Results[0].Value;
    }
  }

  /**
   * Run the Setup KB Generator agent
   */
  async runSetupAgent(): Promise<void> {
    if (this.isRunning || !this.setupAgentId || !this.websiteUrl) return;

    this.isRunning = true;
    this.errorMessage = '';
    this.progressMessage = 'Starting knowledge base generation...';
    this.hasResults = false;
    this.sectionStates = [];

    try {
      // Save Website back to org entity if it changed
      if (this.selectedOrganization && this.websiteUrl !== this.selectedOrganization.Website) {
        this.selectedOrganization.Website = this.websiteUrl;
        await this.selectedOrganization.Save();
      }

      // Generate a session ID
      const sessionId = crypto.randomUUID();

      // Call the RunAIAgent mutation via GraphQL
      const mutation = `
        mutation RunAIAgent(
          $agentId: String!
          $messages: String!
          $sessionId: String!
          $payload: String
        ) {
          RunAIAgent(
            agentId: $agentId
            messages: $messages
            sessionId: $sessionId
            payload: $payload
          ) {
            success
            errorMessage
            executionTimeMs
            result
          }
        }
      `;

      const variables = {
        agentId: this.setupAgentId,
        messages: JSON.stringify([{
          role: 'user',
          content: `Generate the knowledge base for ${this.organizationName}. Website: ${this.websiteUrl}`
        }]),
        sessionId: sessionId,
        payload: JSON.stringify({
          input: {
            websiteUrl: this.websiteUrl,
            organizationName: this.organizationName
          }
        })
      };

      this.progressMessage = 'Crawling website and generating knowledge base (this may take 1-2 minutes)...';

      const gqlResult = await GraphQLDataProvider.ExecuteGQL(mutation, variables);

      if (!gqlResult?.RunAIAgent) {
        throw new Error('No response from RunAIAgent mutation');
      }

      const agentResponse = gqlResult.RunAIAgent;

      if (!agentResponse.success) {
        throw new Error(agentResponse.errorMessage || 'Agent execution failed');
      }

      // Parse the result
      const executionResult = JSON.parse(agentResponse.result);
      const payload = executionResult?.payload;

      if (!payload?.output?.sections) {
        throw new Error('Agent did not return expected output sections');
      }

      // Build review states
      const sections: GeneratedSection[] = payload.output.sections;
      this.crawlSummary = payload.output.crawlSummary || null;

      this.sectionStates = sections.map(section => ({
        section,
        accepted: section.confidence !== 'low',
        isEditing: false,
        editedValue: section.generatedValue,
        renderedHtml: this.renderMarkdown(section.generatedValue),
        isExpanded: false,
        showSources: false
      }));

      this.hasResults = true;
      this.progressMessage = '';

      // Update last run timestamp
      await this.saveLastRunAt();
      await this.loadLastRunAt();

    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.errorMessage = `Setup agent failed: ${msg}`;
      this.progressMessage = '';
    } finally {
      this.isRunning = false;
      this.emitStatus();
    }
  }

  /**
   * Save all accepted sections to organization settings
   */
  async saveAcceptedSections(): Promise<void> {
    if (!this.organizationId) return;

    const accepted = this.sectionStates.filter(s => s.accepted);
    if (accepted.length === 0) return;

    this.progressMessage = 'Saving accepted sections...';
    this.errorMessage = '';

    try {
      const md = new Metadata();

      for (const state of accepted) {
        const settingKey = state.section.settingKey;
        const value = state.isEditing ? state.editedValue : state.section.generatedValue;

        // Find the setting definition
        const setting = this.settings.find(s => s.Key === settingKey);
        if (!setting) continue;

        if (!this.organizationScopeId) continue;

        // Check if an org setting already exists
        const rv = new RunView();
        const existing = await rv.RunView<BCSaaSOrganizationSettingEntity>({
          EntityName: 'BC: Organization Settings',
          ExtraFilter: `OrganizationID = '${this.organizationId}' AND SettingID = '${setting.ID}' AND ScopeID = '${this.organizationScopeId}'`,
          ResultType: 'entity_object'
        });

        if (existing?.Success && existing.Results.length > 0) {
          // Update existing
          const orgSetting = existing.Results[0];
          orgSetting.Value = value;
          await orgSetting.Save();
        } else {
          // Create new
          const newOrgSetting = await md.GetEntityObject<BCSaaSOrganizationSettingEntity>('BC: Organization Settings');
          newOrgSetting.OrganizationID = this.organizationId;
          newOrgSetting.SettingID = setting.ID;
          newOrgSetting.Value = value;
          newOrgSetting.ScopeID = this.organizationScopeId;
          await newOrgSetting.Save();
        }
      }

      this.progressMessage = `${accepted.length} section(s) saved successfully.`;

      // Mark the pending sync result as applied and hide the banner
      if (this.pendingSyncResultId) {
        const rv = new RunView();
        const result = await rv.RunView({
          EntityName: 'Pending Crawl Results',
          ExtraFilter: `ID = '${this.pendingSyncResultId}'`,
          ResultType: 'entity_object'
        });
        if (result?.Success && result.Results.length > 0) {
          const record = result.Results[0] as unknown as { Status: string; Save: () => Promise<boolean> };
          record.Status = 'Dismissed';
          await record.Save();
        }
        this.hasPendingSync = false;
        this.pendingSyncResultId = null;
        this.pendingSyncDate = null;
        this.pendingSyncResultJSON = null;
      }

      // Clear discovered sections after successful save
      this.sectionStates = [];
      this.hasResults = false;

      // Clear results after successful save
      setTimeout(() => {
        this.progressMessage = '';
      }, 3000);

    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.errorMessage = `Error saving sections: ${msg}`;
      this.progressMessage = '';
    }
  }

  private async saveLastRunAt(): Promise<void> {
    if (!this.organizationId) return;

    const md = new Metadata();
    const setting = this.settings.find(s => s.Key === 'SetupAgentLastRunAt');
    if (!setting) return;

    const rv = new RunView();
    const existing = await rv.RunView<BCSaaSOrganizationSettingEntity>({
      EntityName: 'BC: Organization Settings',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND SettingID = '${setting.ID}'`,
      ResultType: 'entity_object'
    });

    const now = new Date().toISOString();

    if (existing?.Success && existing.Results.length > 0) {
      existing.Results[0].Value = now;
      await existing.Results[0].Save();
    } else {
      const newSetting = await md.GetEntityObject<BCSaaSOrganizationSettingEntity>('BC: Organization Settings');
      newSetting.OrganizationID = this.organizationId;
      newSetting.SettingID = setting.ID;
      newSetting.Value = now;
      newSetting.ScopeID = this.organizationScopeId;
      await newSetting.Save();
    }
  }

  // ========== Website Sync Pending Review ==========

  private async loadPendingSync(): Promise<void> {
    if (!this.organizationId) return;

    this.hasPendingSync = false;
    this.pendingSyncDate = null;
    this.pendingSyncResultId = null;
    this.pendingSyncResultJSON = null;

    // Only show pending results if the org has website sync enabled
    const syncEnabled = IzzyMetadataEngine.Instance.GetOrganizationSettingValue('WebsiteSyncEnabled', this.organizationId);
    if (syncEnabled.value !== 'true') return;

    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Pending Crawl Results',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND Status = 'Pending'`,
      OrderBy: 'CrawledAt DESC',
      ResultType: 'simple'
    });

    if (result?.Success && result.Results.length > 0) {
      const row = result.Results[0] as { ID: string; CrawledAt: string; ResultJSON: string };
      this.pendingSyncResultId = row.ID;
      this.pendingSyncDate = row.CrawledAt;
      this.pendingSyncResultJSON = row.ResultJSON;
      this.hasPendingSync = true;
    }
  }

  async reviewPendingSync(): Promise<void> {
    if (!this.pendingSyncResultJSON) return;

    try {
      const payload = JSON.parse(this.pendingSyncResultJSON);
      const sections: GeneratedSection[] = payload?.output?.sections ?? [];

      this.crawlSummary = payload?.output?.crawlSummary ?? null;
      this.sectionStates = sections.map(section => ({
        section,
        accepted: section.confidence !== 'low',
        isEditing: false,
        editedValue: section.generatedValue,
        renderedHtml: this.renderMarkdown(section.generatedValue),
        isExpanded: false,
        showSources: false
      }));

      this.hasResults = true;
    } catch {
      this.errorMessage = 'Could not parse the pending sync result. It may be corrupted.';
    }
  }

  async dismissPendingSync(): Promise<void> {
    if (!this.pendingSyncResultId) return;

    const rv = new RunView();
    const result = await rv.RunView<BCSaaSOrganizationSettingEntity>({
      EntityName: 'Pending Crawl Results',
      ExtraFilter: `ID = '${this.pendingSyncResultId}'`,
      ResultType: 'entity_object'
    });

    if (result?.Success && result.Results.length > 0) {
      const record = result.Results[0] as unknown as { Status: string; Save: () => Promise<boolean> };
      record.Status = 'Dismissed';
      await record.Save();
    }

    this.hasPendingSync = false;
    this.pendingSyncResultId = null;
    this.pendingSyncDate = null;
    this.pendingSyncResultJSON = null;
    if (!this.hasResults) this.sectionStates = [];
  }

  // ========== UI Helpers ==========

  toggleSectionAccept(state: SectionReviewState): void {
    state.accepted = !state.accepted;
  }

  toggleExpand(state: SectionReviewState): void {
    state.isExpanded = !state.isExpanded;
  }

  toggleSources(state: SectionReviewState): void {
    state.showSources = !state.showSources;
  }

  startEditing(state: SectionReviewState): void {
    state.isEditing = true;
    state.editedValue = state.section.generatedValue;
  }

  cancelEditing(state: SectionReviewState): void {
    state.isEditing = false;
    state.editedValue = state.section.generatedValue;
    state.renderedHtml = this.renderMarkdown(state.section.generatedValue);
  }

  applyEdit(state: SectionReviewState): void {
    state.section.generatedValue = state.editedValue;
    state.renderedHtml = this.renderMarkdown(state.editedValue);
    state.isEditing = false;
  }

  toggleSelectAll(): void {
    const allSelected = this.acceptedCount === this.sectionStates.length;
    this.sectionStates.forEach(s => s.accepted = !allSelected);
  }

  get acceptedCount(): number {
    return this.sectionStates.filter(s => s.accepted).length;
  }

  get canRun(): boolean {
    return !!this.setupAgentId && !!this.websiteUrl && !this.isRunning;
  }

  getConfidenceBadgeClass(confidence: string): string {
    switch (confidence) {
      case 'high': return 'badge-high';
      case 'medium': return 'badge-medium';
      case 'low': return 'badge-low';
      default: return 'badge-medium';
    }
  }

  formatDate(isoString: string | null): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) return '';
    const html = marked.parse(content, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private emitStatus(): void {
    if (!this.setupAgentId) {
      this.statusChange.emit({ status: 'warning', tooltip: 'Setup agent not found in database' });
    } else if (this.lastRunAt) {
      this.statusChange.emit({ status: 'good', tooltip: `Last run: ${this.formatDate(this.lastRunAt)}` });
    } else {
      this.statusChange.emit({ status: 'none', tooltip: 'Setup agent available' });
    }
  }
}
